// Звук игры: что звучит на каком событии, мьют и его память между сессиями.
//
// Имена звуков объявлены в каталоге ассетов, поэтому опечатку ловит компилятор, как и с кадрами
// картинок, см. assets/catalog.ts.
//
// Звук один на всю игру и живёт в этом модуле, как и перевод, см. i18n/text.ts. До загрузки
// он молчит: игра зовёт его из любого места, и таскать движок через все конструкторы значило бы
// вести его до кнопки через полигры.
//
// Молчит он и после загрузки, пока игрок не нажал на экран интро: браузер не пускает звук
// без жеста игрока, см. ADR-47. Музыку, заказанную до жеста, звук помнит и заводит на нём же.
//
// Мьют игрок ставит кнопкой в правом верхнем углу поля, см. ui/soundButton.ts. Флаг лежит
// в памяти браузера и переживает перезагрузку страницы: игрок, выключивший звук, не должен
// выключать его снова на каждом заходе.

import type { Cents } from '../api/gameConfig';
import { MUSIC_SPRITE, SFX_SPRITE } from '../assets/catalog';

import { SILENT_ENGINE, type SoundEngine, type SoundVoice } from './engine';

/** Имена звуковых эффектов. */
export type SfxName = keyof typeof SFX_SPRITE.sounds;

/** Имена музыкальных дорожек. */
export type MusicName = keyof typeof MUSIC_SPRITE.sounds;

/**
 * Голос паровоза: приезд машины, её гудок и вся поездка бонуса.
 *
 * Играет их только дым: паровоз подаёт голос — из-под котла бьёт пар, бьёт пар — паровоз
 * подаёт голос, см. ui/smoke.ts и ADR-49. Порознь они не звучат и не показываются.
 *
 * Приездом машина выходит на экран, гудком называет выигрыш. Другого повода гудеть у неё нет:
 * гудок обещает выигрыш, и звучать он должен только за ним.
 *
 * Остальные три ведут поездку бонуса, см. ui/ride.ts и ADR-51: паровоз трогается со станции,
 * стучит колёсами на ходу и тормозит у неё же.
 */
export type TrainSfx = 'trainArrives' | 'trainWhistle' | 'trainStarts' | 'trainRuns' | 'trainStops';

/** Память мьюта: она переживает перезагрузку страницы. */
export interface MuteMemory {
  /** Выключал ли игрок звук в прошлый раз. */
  read(): boolean;

  /** Запоминает выбор игрока. */
  write(muted: boolean): void;
}

/** Под каким именем флаг мьюта лежит в памяти браузера. */
export const MUTE_KEY: string = 'steamSpin.muted';

/** Память, которая ничего не помнит. С ней игра идёт до загрузки звука и в тестах. */
export const FORGETFUL_MEMORY: MuteMemory = { read: (): boolean => false, write: (): void => {} };

/**
 * Память мьюта в local storage браузера.
 *
 * Память может быть закрыта настройками приватности, и обращение к ней тогда бросает. Игру
 * это ронять не должно: звук работает и без памяти, просто мьют забывается с перезагрузкой.
 */
export function browserMemory(): MuteMemory {
  return {
    read: (): boolean => {
      try {
        return localStorage.getItem(MUTE_KEY) === 'true';
      } catch (error: unknown) {
        console.warn('Память браузера закрыта, мьют не прочитан', error);
        return false;
      }
    },
    write: (muted: boolean): void => {
      try {
        localStorage.setItem(MUTE_KEY, String(muted));
      } catch (error: unknown) {
        console.warn('Память браузера закрыта, мьют не запомнен', error);
      }
    },
  };
}

/**
 * Звук выигрыша по его размеру.
 *
 * Звуков три, и порог у каждого свой, см. GDD, раздел 3.4: до двух ставок играет тихий,
 * до пяти — средний, дальше громкий. Выигрыш от десяти ставок — это уже Big Win, и его
 * называют створки со своим звуком, см. game/spinRound.ts.
 *
 * @param payout Выплата за спин.
 * @param stake Ставка, которой сыгран раунд. Ноль в ней невозможен, но делить на него нечего.
 * @returns Имя звука. Null — спин не заплатил, и называть нечего.
 */
export function winSfx(payout: Cents, stake: Cents): SfxName | null {
  if (payout <= 0)
    return null;

  if (stake <= 0)
    return 'winHigh';

  const rate: number = payout / stake;

  if (rate <= 2)
    return 'winLow';

  if (rate <= 5)
    return 'winMiddle';

  return 'winHigh';
}

/** Звук игры: эффекты, музыка и мьют. */
export class GameSound {
  readonly #engine: SoundEngine;
  readonly #memory: MuteMemory;

  #muted: boolean;

  /** Пустил ли браузер звук. До жеста игрока не пустит, сколько ни проси. */
  #unlocked: boolean = false;

  /** Заказанная дорожка. Она играет с той минуты, как звук пущен. */
  #track: MusicName | null = null;

  constructor(engine: SoundEngine, memory: MuteMemory) {
    this.#engine = engine;
    this.#memory = memory;

    // Мьют игрок ставил в прошлый заход, и звук начинает с того, чем игрок кончил.
    this.#muted = memory.read();
    engine.mute(this.#muted);
  }

  /** Выключен ли звук. */
  get muted(): boolean {
    return this.#muted;
  }

  /** Выключает и включает звук. Музыка при этом идёт дальше, её просто не слышно. */
  set muted(value: boolean) {
    if (value === this.#muted)
      return;

    this.#muted = value;
    this.#memory.write(value);
    this.#engine.mute(value);
  }

  /**
   * Пускает звук. Звать сразу за жестом игрока, иначе браузер откажет.
   *
   * Дорожка, заказанная до жеста, заводится здесь: игра просит музыку, а не караулит момент,
   * когда её можно завести.
   */
  unlock(): void {
    if (this.#unlocked)
      return;

    this.#unlocked = true;
    this.#engine.resume();

    if (this.#track !== null)
      this.#engine.loop(this.#track);
  }

  /**
   * Играет звуковой эффект один раз и отдаёт ручку, которой его можно увести.
   *
   * Null — звук сейчас не идёт: браузер его ещё не пустил или атлас не загрузился. Уводить
   * тогда нечего, и зовущий держит null наравне с ручкой, см. ui/smoke.ts.
   */
  sfx(name: SfxName): SoundVoice | null {
    return this.#unlocked ? this.#engine.play(name) : null;
  }

  /**
   * Сколько звук длится в секундах. Ноль — звук не загружен.
   *
   * По ней с звуком идёт в ногу картинка, и длина берётся у звука, а не задаётся второй раз
   * в коде: разойдись они — дым тянулся бы дольше гудка, см. ui/smoke.ts.
   *
   * Мьют длину не трогает: беззвучная игра идёт тем же ходом, что и звучащая.
   */
  seconds(name: SfxName): number {
    return this.#engine.seconds(name);
  }

  /**
   * Ставит музыку вместо той, что идёт сейчас. Null — тишина.
   *
   * Та же дорожка второй раз не заводится: бонус просит свою музыку на каждом запуске,
   * а рвать её ради того же самого нечего.
   */
  music(name: MusicName | null): void {
    if (name === this.#track)
      return;

    this.#track = name;

    if (this.#unlocked)
      this.#engine.loop(name);
  }
}

/** Игра до загрузки звука: молчит и ничего не помнит. */
let current = new GameSound(SILENT_ENGINE, FORGETFUL_MEMORY);

/** Ставит звук, которым игра шумит. */
export function useSound(sound: GameSound): void {
  current = sound;
}

/** Звук, которым игра шумит сейчас. */
export function gameSound(): GameSound {
  return current;
}

/** Играет звуковой эффект один раз и отдаёт ручку, которой его можно увести. */
export function sfx(name: SfxName): SoundVoice | null {
  return current.sfx(name);
}

/** Сколько звук длится в секундах. Ноль — звук не загружен. */
export function sfxSeconds(name: SfxName): number {
  return current.seconds(name);
}

/** Ставит музыку вместо той, что идёт сейчас. Null — тишина. */
export function music(name: MusicName | null): void {
  current.music(name);
}
